import dayjs from 'dayjs';

export function generateTxnNumber(prefix) {
  const date = dayjs().format('YYYYMMDD');
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${date}-${rand}`;
}

export function formatDate(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD MMM YYYY') : '—';
}

export function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('DD MMM YYYY, h:mm A') : '—';
}

export function formatCurrency(value) {
  const n = Number(value);
  if (isNaN(n)) return '—';
  return `₹\u00a0${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Parse server money strings ("1176.0000") safely; NaN → 0. */
export function parseMoney(value) {
  if (value == null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

/** Display INR with ₹ prefix and Indian grouping (alias of formatCurrency for pharmacy receipts). */
export function formatINR(value) {
  return formatCurrency(parseMoney(value));
}

export function formatQty(value) {
  const n = Number(value);
  if (isNaN(n)) return '—';
  if (n % 1 === 0) return n.toLocaleString('en-IN');
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

export function daysUntilExpiry(expiryDate) {
  if (!expiryDate) return null;
  return dayjs(expiryDate).diff(dayjs(), 'day');
}

export function exportToCsv(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map(r =>
      headers.map(h => JSON.stringify(r[h] != null ? r[h] : '')).join(',')
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function lineTotal(qty, rate, taxPct = 0, discPct = 0) {
  const q = Number(qty) || 0;
  const r = Number(rate) || 0;
  const t = Number(taxPct) || 0;
  const d = Number(discPct) || 0;
  return q * r * (1 - d / 100) * (1 + t / 100);
}

/** Gross discount amount before tax (qty × rate × disc%). */
export function computeLineDiscountAmount(qty, rate, discPct = 0) {
  const q = Number(qty) || 0;
  const r = Number(rate) || 0;
  const d = Number(discPct) || 0;
  return q * r * (d / 100);
}

/** PO / procurement line financial rollup for order-summary panels. */
export function computePoFinancials(lines = []) {
  let subtotal = 0;
  let totalTax = 0;
  const taxBuckets = {};
  let lineCount = 0;

  for (const line of lines) {
    if (!line.item_id || !line.ordered_quantity || !line.unit_rate) continue;
    lineCount += 1;
    const q = Number(line.ordered_quantity) || 0;
    const r = Number(line.unit_rate) || 0;
    const t = Number(line.tax_percent) || 0;
    const d = Number(line.discount_percent) || 0;
    const gross = q * r;
    const basic = gross * (1 - d / 100);
    const taxAmt = basic * (t / 100);
    subtotal += basic;
    totalTax += taxAmt;
    if (t > 0) taxBuckets[t] = (taxBuckets[t] || 0) + taxAmt;
  }

  return {
    subtotal,
    totalTax,
    taxBuckets,
    grandTotal: subtotal + totalTax,
    lineCount,
  };
}

/** Strip non-digits — quantity fields accept whole numbers only. */
export function sanitizeQtyInput(raw) {
  return String(raw ?? '').replace(/[^\d]/g, '');
}

/** Positive integer quantity, or null if invalid. */
export function parseWholeQty(raw) {
  const s = String(raw ?? '').trim();
  if (s === '' || !/^\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  return n > 0 ? n : null;
}

/** Non-negative integer (for QC accept qty). */
export function parseWholeQtyOrZero(raw) {
  const s = sanitizeQtyInput(raw);
  if (s === '') return 0;
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Positive rate, or null if invalid. */
export function parsePositiveRate(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveGrnLineFinancials(item, lines = []) {
  const wizardLine = lines.find(l =>
    l.item_id === item.item_id
    || l.po_item_id === (item.purchase_order_item_id || item.po_item_id),
  );
  return {
    rate: Number(item.unit_rate ?? item.purchase_rate ?? wizardLine?.unit_rate) || 0,
    tax: Number(item.tax_percent ?? wizardLine?.tax_percent) || 0,
    disc: Number(item.discount_percent ?? wizardLine?.discount_percent) || 0,
  };
}

/**
 * GRN financial totals — use received qty before QC, accepted qty after rejection.
 */
export function computeGrnFinancials(items, { qcData = {}, useAccepted = false, lines = [] } = {}) {
  let subtotal = 0;
  let totalTax = 0;
  const taxBuckets = {};

  for (const item of items) {
    const id = item.grn_item_id || item.id;
    const received = Math.floor(Number(item.received_quantity) || 0);
    let qty;
    if (useAccepted) {
      const qc = qcData[id];
      qty = qc?.accept_quantity != null
        ? Math.floor(Number(qc.accept_quantity))
        : Math.floor(Number(item.accepted_quantity ?? received));
    } else {
      qty = Math.floor(Number(item.received_quantity) || 0);
    }

    const { rate, tax, disc } = resolveGrnLineFinancials(item, lines);
    const gross = qty * rate;
    const basic = gross * (1 - disc / 100);
    const taxAmt = basic * (tax / 100);

    subtotal += basic;
    totalTax += taxAmt;
    if (tax > 0) taxBuckets[tax] = (taxBuckets[tax] || 0) + taxAmt;
  }

  return {
    subtotal,
    totalTax,
    taxBuckets,
    grandTotal: subtotal + totalTax,
  };
}
